// File: src/English/ShakespeareModule.js

import React, {useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { shuffleArray } from "./EnglishTool/shuffleArray.js";
import {
  buildShakespeareVocabMC,
  buildDramaticDeviceMC,
  buildSpeakerMoodMC,
  buildIambicPentameterMC
} from "./EnglishTool/AdvanceEnglishHelper";
import { shakespeareVocabPool } from "./EnglishTool/pool/shakespeareVocabPool.js";
import { dramaticDevicesPoolsByPlay } from "./EnglishTool/pool/dramaticDevicesPool.js";
import { speakerMoodPoolsByPlay } from "./EnglishTool/pool/speakerMoodPool.js";
import { iambicPentameterPool } from "./EnglishTool/pool/iambicPentameterPool.js";
import {
  StoryModeMultipleChoice,
  StoryModeMultiPart,
  StoryModeFillInBlank,
  StoryModeIdentify,
} from "./EnglishTool/StoryModeStructure";
import SpeakButton from "./EnglishTool/SpeakButton";

// ---- SLIDE-BASED PLAY STRUCTURE ----
const shakespeareCurriculum = {
  grade8: [
    {
      playTitle: "A Midsummer Night’s Dream",
      characters: [
        { name: "Puck (Robin Goodfellow)", description: "Mischievous fairy, servant to Oberon" },
        { name: "Oberon", description: "King of the fairies, feuding with Titania" },
        { name: "Titania", description: "Queen of the fairies, proud and protective of a changeling boy" },
        { name: "Hermia", description: "Young Athenian woman who loves Lysander" },
        { name: "Lysander", description: "In love with Hermia, defies her father’s wishes" },
        { name: "Demetrius", description: "Initially loves Hermia, but becomes enchanted to love Helena" },
        { name: "Helena", description: "Hermia’s friend, desperately in love with Demetrius" },
        { name: "Egeus", description: "Hermia’s father, insists she marry Demetrius" },
        { name: "Theseus", description: "Duke of Athens, about to wed Hippolyta" },
        { name: "Nick Bottom", description: "Weaver who is unwittingly given a donkey’s head" }
      ],
      languageGuide: [
        { archaic: "thou", modern: "you" },
        { archaic: "thy", modern: "your" },
        { archaic: "hath", modern: "has" }
      ],
      slides: [
        {
          title: "Setting the Scene",
          body: (
            <>
              <p>
                In Athens, Duke Theseus is preparing to marry Hippolyta. Meanwhile, Egeus brings his daughter Hermia before the Duke, demanding she marry Demetrius instead of Lysander, the man she loves. Hermia and Lysander plan to flee the city to escape her father’s threat.
              </p>
              <p>
                <b>Hermia’s Father (Egeus) declares:</b> “As she is mine, I may dispose of her.” 
                <SpeakButton text="As she is mine, I may dispose of her." character="Egeus" />
              </p>
              <p>
                <b>Famous Quote (Lysander):</b> “The course of true love never did run smooth.”
                <SpeakButton text="The course of true love never did run smooth." character="Lysander" />
              </p>
            </>
          )
        },
        {
          title: "The Forest of Magic",
          body: (
            <>
              <p>
                Hermia and Lysander slip away into the forest, closely followed by Demetrius (who still pursues Hermia) and Helena (who pines for Demetrius). In this moonlit realm, fairy king Oberon and queen Titania clash over a changeling boy. Oberon, seeking revenge for Titania’s defiance, enlists Puck to use a magic flower that causes people to fall in love with the next living thing they see.
              </p>
              <p>
                <b>Oberon:</b> “Ill met by moonlight, proud Titania.”
                <SpeakButton text="Ill met by moonlight, proud Titania." character="Oberon" />
              </p>
              <p>
                <b>Titania:</b> “What, jealous Oberon! Fairies, skip hence.”
                <SpeakButton text="What, jealous Oberon! Fairies, skip hence." character="Titania" />
              </p>
              <p>
                With a sly grin, Puck plans to meddle not only in Titania’s affairs, but also in the entangled love lives of the young Athenians.
              </p>
            </>
          )
        },
        {
          title: "Chaos and Comedy",
          body: (
            <>
              <p>
                While the Athenian lovers chase each other through the forest, a group of amateur actors, often called “the mechanicals,” rehearse a play for the Duke’s wedding. Among them is Nick Bottom, whose head Puck transforms into that of a donkey. When Titania awakens under the flower’s spell, she beholds Bottom the donkey and instantly falls in love.
              </p>
              <p>
                <b>Puck:</b> “Lord, what fools these mortals be!”
                <SpeakButton text="Lord, what fools these mortals be!" character="Puck" />
              </p>
              <p>
                Oberon, seeing the confusion intensify among the mortal lovers, orders Puck to rectify his pranks. Spells are reversed and the love triangles are sorted out: Demetrius discovers renewed affection for Helena, and Hermia remains devoted to Lysander.
              </p>
              <p>
                <b>Titania (enchanted):</b> “Mine ear is much enamored of thy note.”
                <SpeakButton text="Mine ear is much enamored of thy note." character="Titania" />
              </p>
            </>
          )
        },
        {
          title: "Resolution & Weddings",
          body: (
            <>
              <p>
                By dawn, Oberon releases Titania from the enchantment, and the fairy king and queen reconcile, dancing together in harmony. The lovers believe their wild night to be nothing but a dream. Theseus and Hippolyta discover the sleeping Athenians and bring them back to Athens, where they all celebrate a triple wedding. 
              </p>
              <p>
                <b>Theme:</b> Love may be bewildering and unpredictable, but the play insists upon a joyful resolution for all.
              </p>
              <p>
                <b>Bottom (reflecting on his donkey-headed adventure):</b> “I have had a most rare vision. I have had a dream, past the wit of man to say what dream it was.”
                <SpeakButton text="I have had a most rare vision. I have had a dream, past the wit of man to say what dream it was." character="Bottom" />
              </p>
            </>
          )
        },
        {
          title: "Shakespearean Language Guide",
          body: (
            <>
              <ul>
                <li><b>thou</b> → you</li>
                <li><b>thy</b> → your</li>
                <li><b>hath</b> → has</li>
                <li><b>whence</b> → from where</li>
                <li><b>wherefore</b> → why</li>
                <li><b>fairy</b> → magical being</li>
              </ul>
              <p>Practice: Translate <b>"Thou art more lovely and more temperate."</b> (from Sonnet 18)</p>
            </>
          )
        }
      ],
      generateQuiz: (playTitle = "A Midsummer Night’s Dream") => {
        const questions = [
          buildShakespeareVocabMC(shakespeareVocabPool),
          buildDramaticDeviceMC(dramaticDevicesPoolsByPlay[playTitle] || []),
          buildSpeakerMoodMC(speakerMoodPoolsByPlay),
          buildIambicPentameterMC(iambicPentameterPool)
        ].filter(Boolean);
        return shuffleArray(questions).slice(0, 5);
      }
    }
  ], 
  grade9: [
    {
      playTitle: "Romeo and Juliet",
      characters: [
        { name: "Romeo Montague", description: "Romantic, impulsive young man from the Montague family" },
        { name: "Juliet Capulet", description: "Young Capulet daughter, falls in love with Romeo" },
        { name: "Mercutio", description: "Romeo’s witty and loyal friend" },
        { name: "Benvolio", description: "Romeo’s cousin and friend, peacemaker" },
        { name: "Tybalt", description: "Juliet’s fiery cousin, hates Montagues" },
        { name: "Friar Laurence", description: "Kind priest who helps the lovers" },
        { name: "Nurse", description: "Juliet’s confidante and messenger" }
      ],
      languageGuide: [
        { archaic: "wherefore", modern: "why" },
        { archaic: "art", modern: "are" },
        { archaic: "beseech", modern: "beg" }
      ],
      slides: [
        {
          title: "Feud in Verona: The Setup",
          body: (
            <>
              <p>
                In Verona, two powerful families, the Montagues and Capulets, are locked in an ancient feud. Romeo and Juliet, children of these rival houses, fall in love at first sight—unaware of each other’s identity.
              </p>
              <p>
                <b>Romeo (on love):</b> “O brawling love! O loving hate!” 
                <SpeakButton text="O brawling love! O loving hate!" character="Romeo" />
              </p>
              <p>
                <b>Juliet (questioning fate):</b> “My only love sprung from my only hate!” 
                <SpeakButton text="My only love sprung from my only hate!" character="Juliet" />
              </p>
            </>
          )
        },
        {
          title: "The Balcony Scene & Secret Plans",
          body: (
            <>
              <p>
                In the famous balcony scene, Romeo sneaks into the Capulet garden to see Juliet. They confess their love and vow to marry in secret.
              </p>
              <p>
                <b>Romeo:</b> “But soft! What light through yonder window breaks?”
                <SpeakButton text="But soft! What light through yonder window breaks?" character="Romeo" />
              </p>
              <p>
                <b>Juliet:</b> “O Romeo, Romeo! Wherefore art thou Romeo?”
                <SpeakButton text="O Romeo, Romeo! Wherefore art thou Romeo?" character="Juliet" />
              </p>
            </>
          )
        },
        {
          title: "Tragedy Unfolds",
          body: (
            <>
              <p>
                The secret marriage sets off a chain of misunderstandings and violence. Tybalt kills Mercutio; Romeo, in revenge, kills Tybalt and is banished. Juliet is forced toward marriage with Paris, and the lovers’ plans unravel in tragic miscommunication.
              </p>
              <p>
                <b>Juliet (on desperation):</b> “Give me my Romeo; and, when he shall die, Take him and cut him out in little stars…”
                <SpeakButton text="Give me my Romeo; and, when he shall die, Take him and cut him out in little stars…" character="Juliet" />
              </p>
              <p>
                <b>Friar Laurence (warning):</b> “These violent delights have violent ends.”
                <SpeakButton text="These violent delights have violent ends." character="Friar Laurence" />
              </p>
            </>
          )
        },
        {
          title: "Death & Legacy",
          body: (
            <>
              <p>
                Romeo, believing Juliet dead, takes his own life beside her. Juliet awakens, finds Romeo dead, and ends her own life as well. The tragedy forces the feuding families to reconcile, but at a terrible cost.
              </p>
              <p>
                <b>Prince (moral):</b> “For never was a story of more woe Than this of Juliet and her Romeo.”
                <SpeakButton text="For never was a story of more woe Than this of Juliet and her Romeo." character="Prince" />
              </p>
            </>
          )
        },
        {
          title: "Language Guide & Practice",
          body: (
            <>
              <ul>
                <li><b>wherefore</b> → why</li>
                <li><b>art</b> → are</li>
                <li><b>beseech</b> → beg</li>
                <li><b>banished</b> → sent away/exiled</li>
                <li><b>thou</b> → you</li>
              </ul>
              <p>Practice: Translate <b>“Wherefore art thou Romeo?”</b></p>
            </>
          )
        }
      ],
      generateQuiz: (playTitle = "Romeo and Juliet") => {
        const questions = [
          buildShakespeareVocabMC(shakespeareVocabPool),
          buildDramaticDeviceMC(dramaticDevicesPoolsByPlay[playTitle] || []),
          buildSpeakerMoodMC(speakerMoodPoolsByPlay),
          buildIambicPentameterMC(iambicPentameterPool)
        ].filter(Boolean);
        return shuffleArray(questions).slice(0, 6);
      }
    }
  ],

  grade10: [
    {
      playTitle: "Macbeth",
      characters: [
        { name: "Macbeth", description: "Scottish general, becomes king by murder" },
        { name: "Lady Macbeth", description: "Macbeth’s ambitious wife, drives him to murder" },
        { name: "Banquo", description: "Macbeth’s friend, receives prophecy his descendants will be kings" },
        { name: "Duncan", description: "Good King of Scotland, murdered by Macbeth" },
        { name: "Macduff", description: "Scottish noble, opposes Macbeth, restores order" },
        { name: "The Witches", description: "Three mysterious women who prophesize Macbeth’s fate" }
      ],
      languageGuide: [
        { archaic: "thane", modern: "lord or nobleman" },
        { archaic: "hie", modern: "hurry" },
        { archaic: "thou", modern: "you" }
      ],
      slides: [
        {
          title: "Prophecy and Ambition",
          body: (
            <>
              <p>
                Three witches prophesize that Macbeth will become King of Scotland. Encouraged by Lady Macbeth, he murders King Duncan, taking the throne by force.
              </p>
              <p>
                <b>Witches:</b> “Fair is foul, and foul is fair.”
                <SpeakButton text="Fair is foul, and foul is fair." character="Witches" />
              </p>
              <p>
                <b>Lady Macbeth:</b> “Look like the innocent flower, but be the serpent under’t.”
                <SpeakButton text="Look like the innocent flower, but be the serpent under't." character="Lady Macbeth" />
              </p>
            </>
          )
        },
        {
          title: "Guilt and Madness",
          body: (
            <>
              <p>
                After the murder, Macbeth is wracked with guilt and paranoia. Lady Macbeth, too, is haunted by the crime, leading to madness and tragedy.
              </p>
              <p>
                <b>Macbeth:</b> “Will all great Neptune’s ocean wash this blood clean from my hand?”
                <SpeakButton text="Will all great Neptune’s ocean wash this blood clean from my hand?" character="Macbeth" />
              </p>
              <p>
                <b>Lady Macbeth:</b> “Out, damned spot! Out, I say!”
                <SpeakButton text="Out, damned spot! Out, I say!" character="Lady Macbeth" />
              </p>
            </>
          )
        },
        {
          title: "Downfall and Fate",
          body: (
            <>
              <p>
                Macbeth’s tyrannical rule leads to rebellion and his own downfall. Macduff leads the charge to restore order, and Macbeth is killed in battle.
              </p>
              <p>
                <b>Macduff:</b> “Turn, hell-hound, turn!”
                <SpeakButton text="Turn, hell-hound, turn!" character="Macduff" />
              </p>
              <p>
                <b>Macbeth (last words):</b> “Lay on, Macduff, and damn’d be him that first cries, ‘Hold, enough!’”
                <SpeakButton text="Lay on, Macduff, and damn’d be him that first cries, ‘Hold, enough!’" character="Macbeth" />
              </p>
            </>
          )
        },
        {
          title: "Themes & Analysis",
          body: (
            <>
              <ul>
                <li><b>Ambition vs. Morality</b>: How does unchecked ambition destroy Macbeth?</li>
                <li><b>Fate vs. Free Will</b>: Did Macbeth choose his own end, or was he doomed by prophecy?</li>
                <li><b>Guilt & Madness</b>: How do guilt and fear shape Lady Macbeth’s story?</li>
              </ul>
            </>
          )
        },
        {
          title: "Language Guide & Practice",
          body: (
            <>
              <ul>
                <li><b>thane</b> → lord/nobleman</li>
                <li><b>hie</b> → hurry</li>
                <li><b>thou</b> → you</li>
                <li><b>palter</b> → equivocate/dodge truth</li>
                <li><b>knell</b> → bell, usually for a death</li>
              </ul>
              <p>Practice: Translate <b>“Look like the innocent flower, but be the serpent under’t.”</b></p>
            </>
          )
        }
      ],
      generateQuiz: (playTitle = "Macbeth") => {
        const questions = [
          buildShakespeareVocabMC(shakespeareVocabPool),
          buildDramaticDeviceMC(dramaticDevicesPoolsByPlay[playTitle] || []),
          buildSpeakerMoodMC(speakerMoodPoolsByPlay),
          buildIambicPentameterMC(iambicPentameterPool)
        ].filter(Boolean);
        return shuffleArray(questions).slice(0, 7);
      }
    }
  ],

  grade11: [
    {
      playTitle: "Hamlet",
      characters: [
        { name: "Hamlet", description: "Prince of Denmark, seeks to avenge his father’s death" },
        { name: "Claudius", description: "Hamlet’s uncle, usurper king, murderer of Hamlet’s father" },
        { name: "Gertrude", description: "Hamlet’s mother, queen of Denmark" },
        { name: "Ophelia", description: "Hamlet’s love, daughter of Polonius" },
        { name: "Polonius", description: "Lord Chamberlain, advisor to Claudius" },
        { name: "Horatio", description: "Hamlet’s loyal friend" },
        { name: "Laertes", description: "Ophelia’s brother, seeks revenge for his father’s death" }
      ],
      languageGuide: [
        { archaic: "thine", modern: "yours" },
        { archaic: "ere", modern: "before" },
        { archaic: "doth", modern: "does" }
      ],
      slides: [
        {
          title: "Ghosts & Revenge",
          body: (
            <>
              <p>
                Prince Hamlet is visited by the ghost of his father, who reveals he was murdered by Hamlet’s uncle, Claudius. Hamlet vows revenge.
              </p>
              <p>
                <b>Ghost:</b> “Revenge his foul and most unnatural murder.”
                <SpeakButton text="Revenge his foul and most unnatural murder." character="Ghost" />
              </p>
              <p>
                <b>Hamlet:</b> “O, that this too too solid flesh would melt…”
                <SpeakButton text="O, that this too too solid flesh would melt…" character="Hamlet" />
              </p>
            </>
          )
        },
        {
          title: "Madness and Doubt",
          body: (
            <>
              <p>
                Hamlet feigns madness to investigate Claudius. Meanwhile, Ophelia is driven to madness and death by the court’s intrigue.
              </p>
              <p>
                <b>Hamlet:</b> “To be, or not to be: that is the question.”
                <SpeakButton text="To be, or not to be: that is the question." character="Hamlet" />
              </p>
              <p>
                <b>Ophelia:</b> “There’s rosemary, that’s for remembrance.”
                <SpeakButton text="There’s rosemary, that’s for remembrance." character="Ophelia" />
              </p>
            </>
          )
        },
        {
          title: "Death, Action, & Legacy",
          body: (
            <>
              <p>
                Hamlet finally exposes Claudius but at great cost: nearly every major character dies. The play ends with Fortinbras claiming the throne of Denmark.
              </p>
              <p>
                <b>Hamlet:</b> “The rest is silence.”
                <SpeakButton text="The rest is silence." character="Hamlet" />
              </p>
              <p>
                <b>Horatio:</b> “Now cracks a noble heart. Good night, sweet prince.”
                <SpeakButton text="Now cracks a noble heart. Good night, sweet prince." character="Horatio" />
              </p>
            </>
          )
        },
        {
          title: "Themes & Analysis",
          body: (
            <>
              <ul>
                <li><b>Indecision & Action:</b> Why does Hamlet hesitate to act?</li>
                <li><b>Madness—Real or Pretend?</b> How does Shakespeare blur the lines between sanity and insanity?</li>
                <li><b>Mortality & Legacy:</b> What does Hamlet teach us about death and memory?</li>
              </ul>
            </>
          )
        },
        {
          title: "Language Guide & Practice",
          body: (
            <>
              <ul>
                <li><b>thine</b> → yours</li>
                <li><b>ere</b> → before</li>
                <li><b>doth</b> → does</li>
                <li><b>o’er</b> → over</li>
                <li><b>fie</b> → an exclamation of disgust</li>
              </ul>
              <p>Practice: Translate <b>“To be, or not to be: that is the question.”</b></p>
            </>
          )
        }
      ],
      generateQuiz: (playTitle = "Hamlet") => {
        const questions = [
          buildShakespeareVocabMC(shakespeareVocabPool),
          buildDramaticDeviceMC(dramaticDevicesPoolsByPlay[playTitle] || []),
          buildSpeakerMoodMC(speakerMoodPoolsByPlay),
          buildIambicPentameterMC(iambicPentameterPool),
          {
            questionType: "FITB",
            question: "Translate this quote into modern English: 'To be, or not to be: that is the question.'",
            correctAnswer: null, // or provide a model answer for auto-checking
            checkType: "translation",
            requiredWord: null
          },
          {
            questionType: "IDENTIFY",
            question: "Identify the speaker and emotion in: 'Out, damned spot! Out, I say!'",
            correctAnswers: ["Lady Macbeth", "guilt"]
          }
        ].filter(Boolean);
        return shuffleArray(questions).slice(0, 8);
      }      
    }
  ],

  grade12: [
    {
      playTitle: "Othello",
      characters: [
        { name: "Othello", description: "A Moorish general in Venice, honorable but vulnerable" },
        { name: "Desdemona", description: "Othello’s loyal and loving wife" },
        { name: "Iago", description: "Othello’s ensign, master manipulator and villain" },
        { name: "Cassio", description: "Othello’s loyal lieutenant, victim of Iago’s schemes" },
        { name: "Emilia", description: "Iago’s wife, Desdemona’s confidante" },
        { name: "Roderigo", description: "Suitor to Desdemona, easily manipulated" }
      ],
      languageGuide: [
        { archaic: "moor", modern: "North African Muslim" },
        { archaic: "’tis", modern: "it is" },
        { archaic: "whither", modern: "where" }
      ],
      slides: [
        {
          title: "Jealousy & Manipulation",
          body: (
            <>
              <p>
                Othello, a respected general, secretly marries Desdemona. Iago, feeling slighted, launches a scheme to destroy Othello, manipulating him into believing Desdemona is unfaithful.
              </p>
              <p>
                <b>Iago:</b> “O, beware, my lord, of jealousy: It is the green-eyed monster…”
                <SpeakButton text="O, beware, my lord, of jealousy: It is the green-eyed monster…" character="Iago" />
              </p>
              <p>
                <b>Othello:</b> “She loved me for the dangers I had passed, And I loved her that she did pity them.”
                <SpeakButton text="She loved me for the dangers I had passed, And I loved her that she did pity them." character="Othello" />
              </p>
            </>
          )
        },
        {
          title: "Tragic Downfall",
          body: (
            <>
              <p>
                Consumed by jealousy and suspicion, Othello confronts Desdemona, leading to heartbreak and tragedy. Iago’s machinations are revealed, but too late to save the innocent.
              </p>
              <p>
                <b>Desdemona:</b> “I do myself. Farewell. Commend me to my kind lord.” (her last words)
                <SpeakButton text="I do myself. Farewell. Commend me to my kind lord." character="Desdemona" />
              </p>
              <p>
                <b>Othello:</b> “Then must you speak of one that loved not wisely but too well…”
                <SpeakButton text="Then must you speak of one that loved not wisely but too well…" character="Othello" />
              </p>
            </>
          )
        },
        {
          title: "Themes & Social Issues",
          body: (
            <>
              <ul>
                <li><b>Jealousy & Trust:</b> How does Iago create suspicion and destroy trust?</li>
                <li><b>Race & Outsider Status:</b> How does Othello’s identity shape his fate?</li>
                <li><b>The Power of Manipulation:</b> How does language itself become a weapon?</li>
              </ul>
            </>
          )
        },
        {
          title: "Language Guide & Practice",
          body: (
            <>
              <ul>
                <li><b>moor</b> → North African Muslim</li>
                <li><b>’tis</b> → it is</li>
                <li><b>whither</b> → where</li>
                <li><b>cuckold</b> → man whose wife is unfaithful</li>
                <li><b>strumpet</b> → derogatory word for a promiscuous woman</li>
              </ul>
              <p>Practice: Translate <b>“It is the cause, it is the cause, my soul.”</b></p>
            </>
          )
        }
      ],
      generateQuiz: (playTitle = "Othello") => {
        const questions = [
          buildShakespeareVocabMC(shakespeareVocabPool),
          buildDramaticDeviceMC(dramaticDevicesPoolsByPlay[playTitle] || []),
          buildSpeakerMoodMC(speakerMoodPoolsByPlay),
          buildIambicPentameterMC(iambicPentameterPool)
        ].filter(Boolean);
        return shuffleArray(questions).slice(0, 8);
      }
    }
  ]
};

function buildShakespeareFinalQuiz(currentGrade = "grade10") {
    let bigSet = [];
    // Aggregate questions from all plays in the grade
    (shakespeareCurriculum[currentGrade] || []).forEach(play => {
      if (play.generateQuiz) {
        bigSet = bigSet.concat(play.generateQuiz(play.playTitle));
      }
    });
    // You may want more creative/essay questions for higher grades, add here as needed!
    return shuffleArray(bigSet).slice(0, 10); // Up to 10 diverse questions
  }


export default function ShakespeareModule() {
  const [currentGrade, setCurrentGrade] = useState("grade8");
  const [playIdx, setPlayIdx] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);
  const [lastFeedback, setLastFeedback] = useState(null);
  const [user, setUser] = useState(null);

  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes, can adjust
  const hasAwardedTokens = useRef(false);

  const playsForGrade = shakespeareCurriculum[currentGrade] || [];
  const currentPlay = playsForGrade[playIdx] || {};

  useEffect(() => {
    if (fFin && showFinal && !hasAwardedTokens.current) {
      const percent = Math.round((fScore / finalQs.length) * 100);
      alert(`Shakespeare Final Quiz Completed! Score: ${fScore}/${finalQs.length} (${percent}%)`);
      // You can award tokens here if needed (add a callback prop etc)
      hasAwardedTokens.current = true;
    }
  }, [fFin, showFinal, fScore, finalQs.length]);

  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("loggedInUser"));
    if (savedUser) setUser(savedUser);
  }, []);

  // Reset slideIdx and quiz when switching play/grade
  useEffect(() => {
    setSlideIdx(0);
    setShowQuiz(false);
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setLastFeedback(null);
  }, [currentGrade, playIdx]);

  useEffect(() => {
    if (showFinal && !fFin && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFFin(true);
    }
  }, [showFinal, fFin, timeLeft]);
  
  function startQuiz() {
    if (!currentPlay?.generateQuiz) return;
    const newQuiz = currentPlay.generateQuiz(currentPlay.playTitle);
    setQuiz(newQuiz);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(true);
    setLastFeedback(null);
  }

  // Quiz with feedback
  function handleAnswer(opt) {
    const cQ = quiz[qIdx];
    // MC_MULTI: feedback optional, treat as correct if object matches
    if (cQ.questionType === "MC_MULTI" && typeof opt === "object") {
      setQScore(s => s + 1);
      setLastFeedback({
        isCorrect: true,
        message: "Correct!",
        correctAnswer: "N/A"
      });
      setTimeout(() => {
        setLastFeedback(null);
        nextQuestion();
      }, 600);
      return;
    }
    // MC with string answer
    if (opt === cQ.correctAnswer) {
      setQScore(s => s + 1);
      setLastFeedback({ isCorrect: true, message: "Correct!", correctAnswer: cQ.correctAnswer });
      setTimeout(() => {
        setLastFeedback(null);
        nextQuestion();
      }, 600);
    } else {
      setLastFeedback({
        isCorrect: false,
        message: `Not quite. The correct answer was: "${cQ.correctAnswer}".`,
        correctAnswer: cQ.correctAnswer
      });
    }
  }

  function handleFinalAnswer(opt) {
    const cQ = finalQs[fIdx];
    if (!cQ) return;
    // MC and MC_MULTI
    if (cQ.questionType === "MC" || cQ.questionType === "MC_MULTI") {
      if (opt === cQ.correctAnswer) setFScore(s => s + 1);
    } else if (cQ.questionType === "FITB" || cQ.questionType === "IDENTIFY") {
      setFScore(s => s + 1); // Always give credit for now
    }
    if (fIdx < finalQs.length - 1) setFIdx(i => i + 1);
    else setFFin(true);
  }

  
  function nextQuestion() {
    if (qIdx < quiz.length - 1) {
      setQIdx(i => i + 1);
    } else {
      setQuizFin(true);
    }
  }

  function resetQuiz() {
    setShowQuiz(false);
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setLastFeedback(null);
  }

  function startFinalQuiz() {
    const finalSet = buildShakespeareFinalQuiz(currentGrade);
    setFinalQs(finalSet);
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setTimeLeft(300); // reset timer
    setShowFinal(true);
  }

  // Slide navigation for lessons
  function renderPlayContent() {
    if (!currentPlay || !currentPlay.slides) return <p>No play data for this grade.</p>;
    const slides = currentPlay.slides;
    const slide = slides[slideIdx];
    const isLastSlide = slideIdx === slides.length - 1;
  
    return (
      <div style={{ marginTop: 20 }}>
        <h2>{currentPlay.playTitle}</h2>
        {slide.title && <h3>{slide.title}</h3>}
  
        {/* Main slide body */}
        <div>{slide.body}</div>
  
        {/* Characters (first slide only) */}
        {slideIdx === 0 && (
          <>
            <h4>Characters:</h4>
            <ul>
              {currentPlay.characters?.map((c, idx) => (
                <li key={idx}>
                  <b>{c.name}</b>: {c.description}
                </li>
              ))}
            </ul>
          </>
        )}
  
        {/* Language Guide (last slide only) */}
        {isLastSlide && (
          <>
            <h4>Shakespearean Language Guide</h4>
            <ul>
              {currentPlay.languageGuide?.map((lg, idx) => (
                <li key={idx}>
                  <b>{lg.archaic}</b> → {lg.modern}
                </li>
              ))}
            </ul>
          </>
        )}
  
        {/* --- NAVIGATION BUTTONS ALWAYS LAST --- */}
        <div style={{ margin: "24px 0" }}>
          {slideIdx > 0 && (
            <button className="button1" onClick={() => setSlideIdx(i => i - 1)}>
              Back
            </button>
          )}
          {!isLastSlide && (
            <button
              className="button1"
              style={{ marginLeft: 10 }}
              onClick={() => setSlideIdx(i => i + 1)}
            >
              Next
            </button>
          )}
          {isLastSlide && (
            <button
              className="button1"
              style={{ marginLeft: 10 }}
              onClick={startQuiz}
            >
              Take Quiz on {currentPlay.playTitle}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Quiz rendering with feedback
  function renderQuiz() {
    if (quizFin) {
      return (
        <div className="quiz-result-screen">
          <h3>Quiz Finished!</h3>
          <p>You scored {qScore} / {quiz.length}.</p>
          <button className="button1" onClick={resetQuiz}>Return to Play Overview</button>
        </div>
      );
    }

    const cQ = quiz[qIdx];
    if (!cQ) return <p>Loading quiz...</p>;

    switch (cQ.questionType) {
      case "MC":
        return (
          <StoryModeMultipleChoice
            cQ={cQ}
            onAnswerClick={handleAnswer}
            currentScore={qScore}
            quizLength={quiz.length}
            user={user}
            lastFeedback={lastFeedback}
            setLastFeedback={setLastFeedback}
          />
        );
        case "MC_MULTI":
            return (
              <StoryModeMultiPart
                cQ={cQ}
                onAnswerClick={handleAnswer}   // <-- Fix: use onAnswerClick instead of onSubmit
                currentScore={qScore}
                quizLength={quiz.length}
              />
            );
            case "FITB":
                return (
                  <StoryModeFillInBlank
                    cQ={cQ}
                    onSubmit={handleAnswer}
                    currentScore={qScore}
                    quizLength={quiz.length}
                    user={user}
                    lastFeedback={lastFeedback}
                    setLastFeedback={setLastFeedback}
                  />
                );
              case "IDENTIFY":
                return (
                  <StoryModeIdentify
                    cQ={cQ}
                    onSubmit={handleAnswer}
                    currentScore={qScore}
                    quizLength={quiz.length}
                    user={user}
                    lastFeedback={lastFeedback}
                    setLastFeedback={setLastFeedback}
                  />
                );
      default:
        return <p>Unknown question type</p>;
    }
  }

  function renderFinalQuiz() {
    if (fFin) {
      return (
        <div className="quiz-result-screen">
          <h3>Shakespeare Final Quiz Finished!</h3>
          <p>Your final score: {fScore} / {finalQs.length}.</p>
          <button className="button1" onClick={() => setShowFinal(false)}>Return to Module</button>
        </div>
      );
    }
    const cQ = finalQs[fIdx];
    if (!cQ) return <p>Loading final quiz...</p>;
    return (
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: 10 }}>
          Time Left: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
        </div>
        {/* Your quiz rendering code below */}
        {(() => {
          switch (cQ.questionType) {
            case "MC":
              return (
                <StoryModeMultipleChoice
                  cQ={cQ}
                  onAnswerClick={handleFinalAnswer}
                  currentScore={fScore}
                  quizLength={finalQs.length}
                  user={user}
                />
              );
            case "MC_MULTI":
              return (
                <StoryModeMultiPart
                  cQ={cQ}
                  onAnswerClick={handleFinalAnswer}
                  currentScore={fScore}
                  quizLength={finalQs.length}
                />
              );
            case "FITB":
              return (
                <StoryModeFillInBlank
                  cQ={cQ}
                  onSubmit={handleFinalAnswer}
                  currentScore={fScore}
                  quizLength={finalQs.length}
                  user={user}
                />
              );
            case "IDENTIFY":
              return (
                <StoryModeIdentify
                  cQ={cQ}
                  onSubmit={handleFinalAnswer}
                  currentScore={fScore}
                  quizLength={finalQs.length}
                  user={user}
                />
              );
            default:
              return <p>Unknown question type</p>;
          }
        })()}
      </div>
    );
  }
  

  
  return (
    <div className="shakespeare-module-page">
      <h1>Comprehensive Shakespeare & Drama (Grades 8–12)</h1>
      <div style={{ margin: "10px 0" }}>
        <label>Select Grade: </label>
        <select
          className="button1"
          value={currentGrade}
          onChange={e => {
            setCurrentGrade(e.target.value);
            setPlayIdx(0);
            setSlideIdx(0);
            resetQuiz();
          }}
        >
          <option value="grade8">Grade 8</option>
          <option value="grade9">Grade 9</option>
          <option value="grade10">Grade 10</option>
          <option value="grade11">Grade 11</option>
          <option value="grade12">Grade 12</option>
        </select>
      </div>

      {/* Play Selector (if multiple plays per grade) */}
      {playsForGrade.length > 1 && (
        <div style={{ margin: "10px 0" }}>
          <label>Select Play: </label>
          <select
            value={playIdx}
            onChange={e => {
              setPlayIdx(parseInt(e.target.value, 10));
              setSlideIdx(0);
              resetQuiz();
            }}
          >
            {playsForGrade.map((p, idx) => (
              <option key={idx} value={idx}>
                {p.playTitle}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* If not in quiz mode, show the lesson content */}
      {!showQuiz && renderPlayContent()}

      {/* Quiz UI */}
      {showQuiz && renderQuiz()}

      {/* Button to start the final quiz (only when not showing a play quiz or final quiz) */}
      {!showQuiz && !showFinal && (
        <div style={{ margin: "20px 0" }}>
          <button className="button1" onClick={startFinalQuiz}>
            Take Shakespeare Final Quiz
          </button>
        </div>
      )}

      {/* Render the final quiz when active */}
      {showFinal && renderFinalQuiz()}

      <br />
      <Link to="/english" className="button1">← Back to English Page</Link>

    </div>
  );
}
